//@flow
import React, {useEffect, useMemo} from 'react';
import {useFormContext} from 'react-hook-form';
import {useLocation} from 'react-router-dom';
import {Chip, makeStyles, Typography} from '@material-ui/core';
import {TRIGGER_FIELDS} from '../../../const';
import {
  FILTER_TYPES,
  getCustomerInactivityDayOptions,
} from '../../../../../utils/triggerConfig';
import {
  FILTER_SET_VALUE_OPTIONS,
  setFilterFieldUpdates,
} from '../../../../../utils/filterFields';
import {
  DEFAULT_EXECUTION_HOUR_SLOT,
  STANDARD_HOUR_OPTIONS,
} from '../../../../../../constants/executionTime';
import {convertToLocalTime} from '../../../../../../../../utils/date';
import type {CustomerInactivityFilter as CustomerInactivityFilterValues} from '../../../../../../../../entities';
import {colors} from '../../../../../../../../assets/styles/colors';
import {getFormError} from '../../../../../utils/formErrors';
import {ENABLE_WHATSAPP_WEB_PROVIDER_SEARCH_PARAM} from '../../../../../../../crm-campaigns/components/crm-campaign-form';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
  },
  label: {
    fontSize: '.875rem',
    fontWeight: 600,
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '.5rem',
  },
  chip: {
    fontWeight: 600,
    cursor: 'pointer',
  },
  chipSelected: {
    backgroundColor: colors.purple3,
    color: '#fff',
    '&:hover': {
      backgroundColor: colors.purple3,
    },
  },
  fieldError: {
    fontSize: '.8125rem',
    color: colors.danger,
  },
}));

type Props = {
  filterType: typeof FILTER_TYPES.CUSTOMER_INACTIVITY,
};

export default function CustomerInactivityFilterComponent({filterType}: Props) {
  const classes = useStyles();
  const location = useLocation();
  const {register, watch, setValue, getValues, errors} = useFormContext();

  const fieldPrefix = `${TRIGGER_FIELDS.filters}.${filterType}`;

  useEffect(() => {
    register(`${fieldPrefix}.inactiveDays`);
    register(TRIGGER_FIELDS.executionTimeSlot);
  }, [register, fieldPrefix]);

  const allFilters = watch(TRIGGER_FIELDS.filters) || {};
  const filter: CustomerInactivityFilterValues = allFilters[filterType] || {
    inactiveDays: 7,
  };

  const searchParams = new URLSearchParams(location?.search);
  const enableInactivityDayZero = searchParams.has(
    ENABLE_WHATSAPP_WEB_PROVIDER_SEARCH_PARAM
  );

  // Keep 0 visible for hydrated edits when the flag is off so existing test
  // triggers remain editable (same spirit as WA Web provider exception).
  const dayOptions = useMemo(
    () =>
      getCustomerInactivityDayOptions({
        includeDayZero: enableInactivityDayZero,
        currentInactiveDays: filter.inactiveDays,
      }),
    [enableInactivityDayZero, filter.inactiveDays]
  );

  const selectedHourSlot =
    watch(TRIGGER_FIELDS.executionTimeSlot) ||
    filter.executionTimeSlot ||
    DEFAULT_EXECUTION_HOUR_SLOT;

  const inactiveDaysError = getFormError(errors, `${fieldPrefix}.inactiveDays`);
  const executionTimeError = getFormError(
    errors,
    TRIGGER_FIELDS.executionTimeSlot
  );

  const handleSelectDays = (days: number) => {
    setFilterFieldUpdates(
      getValues,
      setValue,
      filterType,
      {inactiveDays: days},
      FILTER_SET_VALUE_OPTIONS
    );
  };

  const handleSelectHourSlot = (slotValue: string) => {
    setValue(
      TRIGGER_FIELDS.executionTimeSlot,
      slotValue,
      FILTER_SET_VALUE_OPTIONS
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.field}>
        <Typography className={classes.label}>
          Hasta X días sin respuesta
        </Typography>
        <div className={classes.chipsRow}>
          {dayOptions.map(days => (
            <Chip
              key={days}
              label={days === 0 ? '0 días (prueba)' : `${days} días`}
              clickable
              className={`${classes.chip} ${
                filter.inactiveDays === days ? classes.chipSelected : ''
              }`}
              color={filter.inactiveDays === days ? 'primary' : 'default'}
              onClick={() => handleSelectDays(days)}
            />
          ))}
        </div>
        {inactiveDaysError && (
          <Typography className={classes.fieldError}>
            {inactiveDaysError}
          </Typography>
        )}
      </div>

      <div className={classes.field}>
        <Typography className={classes.label}>Hora de ejecución</Typography>
        <div className={classes.chipsRow}>
          {STANDARD_HOUR_OPTIONS.map(option => (
            <Chip
              key={option.value}
              label={`${option.label} - ${convertToLocalTime(option.utcValue)}`}
              clickable
              className={`${classes.chip} ${
                selectedHourSlot === option.value ? classes.chipSelected : ''
              }`}
              color={selectedHourSlot === option.value ? 'primary' : 'default'}
              onClick={() => handleSelectHourSlot(option.value)}
            />
          ))}
        </div>
        {executionTimeError && (
          <Typography className={classes.fieldError}>
            {executionTimeError}
          </Typography>
        )}
      </div>
    </div>
  );
}
